import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useAuth, API } from "@/App";
import { useLanguage } from "@/contexts/LanguageContext";
import { PlateMindLogo } from "@/components/PlateMindLogo";
import { BottomNav } from "@/components/BottomNav";
import { Paywall } from "@/components/Paywall";
import { LogoutConfirmDialog } from "@/components/ConfirmDialogs";
import GlucoseCheckIn from "@/components/GlucoseCheckIn";
import CareTeam from "@/components/CareTeam";
import MyMealPlan from "@/components/MyMealPlan";
import { 
  Apple, Search, Droplet, LineChart, MessageCircle, 
  Home, ChevronRight, TrendingUp, TrendingDown,
  Clock, AlertTriangle, User, LogOut, Sparkles,
  Shield, Stethoscope, Building2, Users, Utensils, Camera,
  Flame, Trophy, Target, Zap, Award, Hash
} from "lucide-react";
import { toast } from "sonner";

// Glucose unit conversion helpers - use exact /18 formula
const convertMgdlToMmol = (value) => Math.round((value / 18) * 10) / 10;

// Thresholds for each unit
const THRESHOLDS = {
  "mg/dL": { low: 70, high: 180 },
  "mmol/L": { low: 3.9, high: 10.0 }
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const { t, tArray } = useLanguage();
  const [todayGlucose, setTodayGlucose] = useState([]);
  const [recentMeals, setRecentMeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usageData, setUsageData] = useState(null);
  const [showPaywall, setShowPaywall] = useState(false);
  const [paywallTrigger, setPaywallTrigger] = useState("general");
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [challengeData, setChallengeData] = useState(null);
  const [mealFeedback, setMealFeedback] = useState([]);
  const [challengeRankings, setChallengeRankings] = useState(null);
  
  // Random greeting message - selected once when component mounts
  const greetingMessages = tArray('dashboard.greeting_messages');
  const greetingMessage = useMemo(() => {
    return greetingMessages[Math.floor(Math.random() * greetingMessages.length)];
  }, [greetingMessages]);

  // Get user's preferred glucose unit
  const glucoseUnit = user?.glucose_unit || "mg/dL";
  const thresholds = THRESHOLDS[glucoseUnit];

  // Helper to get display value in user's preferred unit
  const getDisplayValue = (log) => {
    if (log.display_value !== undefined) return log.display_value;
    if (glucoseUnit === "mmol/L") return convertMgdlToMmol(log.glucose_value);
    return log.glucose_value;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [glucoseRes, mealsRes, usageRes] = await Promise.all([
          fetch(`${API}/glucose/today`, {
            headers: { Authorization: `Bearer ${token}` },
            
          }),
          fetch(`${API}/meals?limit=3`, {
            headers: { Authorization: `Bearer ${token}` },
            
          }),
          fetch(`${API}/user/usage`, {
            headers: { Authorization: `Bearer ${token}` },
            
          }),
        ]);

        if (glucoseRes.ok) {
          const glucoseData = await glucoseRes.json();
          setTodayGlucose(glucoseData);
        }

        if (mealsRes.ok) {
          const mealsData = await mealsRes.json();
          setRecentMeals(mealsData);
        }

        if (usageRes.ok) {
          const usage = await usageRes.json();
          setUsageData(usage);
        }
      } catch (error) {
        console.error("Dashboard fetch error:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchChallenge = async () => {
      try {
        const res = await fetch(`${API}/corporate/my-challenge`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.enrolled) setChallengeData(data);
        }
        // Fetch recent meal feedback from coach
        const fbRes = await fetch(`${API}/corporate/my-meals/feedback`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (fbRes.ok) {
          const fbData = await fbRes.json();
          setMealFeedback(fbData.meals || []);
        }
        // Fetch 3-category rankings
        const rankRes = await fetch(`${API}/corporate/my-challenge/rankings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (rankRes.ok) {
          const rankData = await rankRes.json();
          if (rankData.enrolled) setChallengeRankings(rankData);
        }
      } catch (err) { /* not enrolled - ignore */ }
    };

    fetchData();
    fetchChallenge();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleLogout = async () => {
    setLogoutLoading(true);
    await logout();
    toast.success(t('dashboard.logged_out_success'));
    setShowLogoutDialog(false);
    setLogoutLoading(false);
    navigate("/");
  };

  const handleLogoutClick = () => {
    setShowLogoutDialog(true);
  };

  const getGlucoseStatus = (displayValue) => {
    if (displayValue < thresholds.low) return { label: t('glucose.low'), color: "text-destructive", bg: "bg-destructive/10" };
    if (displayValue > thresholds.high) return { label: t('glucose.high'), color: "text-warning", bg: "bg-warning/10" };
    return { label: t('glucose.in_range'), color: "text-success", bg: "bg-success/10" };
  };

  const getReadingTypeLabel = (type) => {
    const labels = {
      fasting: t('glucose.fasting'),
      before_meal: t('glucose.before_meal'),
      after_meal: t('glucose.after_meal'),
      before_bed: t('glucose.before_bed'),
      random: t('glucose.random'),
    };
    return labels[type] || type;
  };

  const latestGlucose = todayGlucose[todayGlucose.length - 1];
  const latestDisplayValue = latestGlucose ? getDisplayValue(latestGlucose) : null;
  
  // Wellness user detection — non-diabetic wellness users get a simplified dashboard
  const isWellnessUser = user?.program_type === "wellness" && (!user?.diabetes_type || user?.diabetes_type === "none" || user?.diabetes_type === "monitoring");
  
  // Calculate average in display unit
  const averageGlucose = todayGlucose.length > 0 
    ? (() => {
        const avgRaw = todayGlucose.reduce((sum, log) => sum + log.glucose_value, 0) / todayGlucose.length;
        if (glucoseUnit === "mmol/L") {
          return convertMgdlToMmol(avgRaw);
        }
        return Math.round(avgRaw);
      })()
    : null;

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="sticky top-0 z-40 w-full border-b border-orange-100 bg-background/80 backdrop-blur-md">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <PlateMindLogo size={40} />
            <span className="font-heading font-bold text-xl text-secondary">platemind</span>
          </div>
          <button
            onClick={handleLogoutClick}
            data-testid="header-logout-btn"
            className="flex items-center gap-2 px-3 py-2 rounded-full hover:bg-orange-50 transition-colors text-muted-foreground hover:text-secondary"
          >
            <LogOut className="w-5 h-5" />
            <span className="hidden sm:inline text-sm font-medium">{t('nav.logout')}</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Welcome Section - Avatar links to Profile */}
        <Link 
          to="/profile" 
          className="flex items-center gap-4 cursor-pointer hover:opacity-80 transition-opacity"
          data-testid="greeting-profile-link"
        >
          <div className="w-14 h-14 rounded-full overflow-hidden bg-primary flex items-center justify-center flex-shrink-0">
            {user?.profile_photo ? (
              <img src={user.profile_photo} alt={user.name} className="w-full h-full object-cover" />
            ) : (
              <span className="text-white font-bold text-2xl">
                {user?.name?.charAt(0)?.toUpperCase() || "U"}
              </span>
            )}
          </div>
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-secondary">
              {t('dashboard.greeting')}, {user?.name?.split(" ")[0] || "there"}
            </h1>
            <p className="text-muted-foreground">{greetingMessage}</p>
          </div>
        </Link>

        {/* Daily Glucose Check-In — Hidden for non-diabetic wellness users */}
        {!isWellnessUser && (
          <GlucoseCheckIn 
            onComplete={(data) => {
              const fetchGlucose = async () => {
                try {
                  const res = await fetch(`${API}/glucose/today`, {
                    headers: { Authorization: `Bearer ${token}` },
                  });
                  if (res.ok) {
                    const glucoseData = await res.json();
                    setTodayGlucose(glucoseData);
                  }
                } catch (error) {
                  console.error("Fetch glucose error:", error);
                }
              };
              fetchGlucose();
            }}
          />
        )}

        {/* Care Team Card (for B2B patients — hidden for wellness) */}
        {!isWellnessUser && <CareTeam />}

        {/* Meal Plan Card (for patients with assigned plans — hidden for wellness) */}
        {!isWellnessUser && <MyMealPlan />}

        {/* Role-based Dashboard Access */}
        {(user?.role === "admin" || user?.role === "dietitian" || user?.role === "partner_admin") && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {user?.role === "admin" && (
              <Link to="/admin" className="block">
                <Card className="p-4 rounded-2xl border-purple-200 bg-purple-50/50 hover:shadow-md transition-all">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                      <Shield className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium text-secondary">{t('dashboard.admin_dashboard')}</p>
                      <p className="text-xs text-muted-foreground">{t('dashboard.manage_platform')}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-purple-400 ml-auto" />
                  </div>
                </Card>
              </Link>
            )}
            {(user?.role === "admin" || user?.role === "dietitian") && (
              <Link to="/physician" className="block">
                <Card className="p-4 rounded-2xl border-blue-200 bg-blue-50/50 hover:shadow-md transition-all">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                      <Stethoscope className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-secondary">{t('dashboard.physician_dashboard')}</p>
                      <p className="text-xs text-muted-foreground">{t('dashboard.monitor_patients')}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-blue-400 ml-auto" />
                  </div>
                </Card>
              </Link>
            )}
            {(user?.role === "admin" || user?.role === "partner_admin") && (
              <Link to="/partner" className="block">
                <Card className="p-4 rounded-2xl border-green-200 bg-green-50/50 hover:shadow-md transition-all">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-secondary">{t('dashboard.partner_dashboard')}</p>
                      <p className="text-xs text-muted-foreground">{t('dashboard.organization_view')}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-green-400 ml-auto" />
                  </div>
                </Card>
              </Link>
            )}
          </div>
        )}

        {/* Find a Healthcare Provider - Hidden for wellness users (they already have a coach) */}
        {!user?.has_care_team && !isWellnessUser && (
          <Link to="/find-provider" className="block">
            <Card
              data-testid="find-provider-card"
              className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-3xl border border-teal-200/50 p-5 hover:shadow-soft-lift transition-all cursor-pointer"
            >
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-teal-100 flex items-center justify-center flex-shrink-0">
                  <Users className="w-7 h-7 text-teal-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-heading font-semibold text-secondary">{t('dashboard.find_healthcare_provider')}</h3>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {t('dashboard.get_personalized_guidance')}
                  </p>
                </div>
                <ChevronRight className="w-5 h-5 text-teal-500 flex-shrink-0" />
              </div>
            </Card>
          </Link>
        )}

        {/* My Meal Plans - Only for B2C users without care team */}
        {user?.user_type === "b2c" && !user?.has_care_team && (
          <Link to="/my-meal-plans" className="block">
            <Card
              data-testid="my-meal-plans-card"
              className="bg-gradient-to-r from-purple-50 to-violet-50 rounded-3xl border border-purple-200/50 p-5 hover:shadow-soft-lift transition-all cursor-pointer"
            >
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <Utensils className="w-7 h-7 text-purple-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-heading font-semibold text-secondary">{t('dashboard.my_meal_plans')}</h3>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {t('dashboard.create_upload_plans')}
                  </p>
                </div>
                <ChevronRight className="w-5 h-5 text-purple-500 flex-shrink-0" />
              </div>
            </Card>
          </Link>
        )}

        {/* Free Plan Usage Indicators */}
        {usageData && !usageData.is_premium && (
          <Card className="bg-gradient-to-r from-orange-50 to-primary/5 rounded-2xl border border-orange-100/50 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-secondary">{t('dashboard.free_plan')}</span>
              </div>
              <button 
                onClick={() => {
                  setPaywallTrigger("general");
                  setShowPaywall(true);
                }}
                className="flex items-center gap-1 text-xs text-primary font-medium hover:underline"
                data-testid="upgrade-link"
              >
                <Sparkles className="w-3 h-3" />
                {t('common.upgrade')}
              </button>
            </div>
            
            {/* Meal Analysis Usage */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{t('dashboard.meal_analyses_used')}</span>
                <span className={`font-medium ${
                  usageData.meal_analysis.used >= usageData.meal_analysis.limit 
                    ? "text-warning" 
                    : "text-secondary"
                }`}>
                  {usageData.meal_analysis.used} / {usageData.meal_analysis.limit}
                </span>
              </div>
              <div className="h-2 bg-white rounded-full overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all ${
                    usageData.meal_analysis.used >= usageData.meal_analysis.limit 
                      ? "bg-warning" 
                      : usageData.meal_analysis.used >= 2 
                        ? "bg-warning/70" 
                        : "bg-primary"
                  }`}
                  style={{ width: `${Math.min((usageData.meal_analysis.used / usageData.meal_analysis.limit) * 100, 100)}%` }}
                />
              </div>
            </div>
            
            {/* Lana Questions Usage */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{t('dashboard.lana_questions_used')}</span>
                <span className={`font-medium ${
                  usageData.lana_questions.used >= usageData.lana_questions.limit 
                    ? "text-warning" 
                    : "text-secondary"
                }`}>
                  {usageData.lana_questions.used} / {usageData.lana_questions.limit}
                </span>
              </div>
              <div className="h-2 bg-white rounded-full overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all ${
                    usageData.lana_questions.used >= usageData.lana_questions.limit 
                      ? "bg-warning" 
                      : usageData.lana_questions.used >= 4 
                        ? "bg-warning/70" 
                        : "bg-primary"
                  }`}
                  style={{ width: `${Math.min((usageData.lana_questions.used / usageData.lana_questions.limit) * 100, 100)}%` }}
                />
              </div>
            </div>

            {/* Upgrade hint when approaching limits */}
            {(usageData.meal_analysis.used >= 2 || usageData.lana_questions.used >= 4) && (
              <p className="text-xs text-muted-foreground pt-1">
                {usageData.meal_analysis.used >= 2 
                  ? `You've analyzed ${usageData.meal_analysis.used} of your ${usageData.meal_analysis.limit} free meals.`
                  : `You've used ${usageData.lana_questions.used} of your ${usageData.lana_questions.limit} free Lana questions.`
                }
              </p>
            )}
          </Card>
        )}

        {/* Today's Glucose Summary — Hidden for wellness users */}
        {!isWellnessUser && <Card className="bg-white rounded-3xl border border-orange-100/50 p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-lg font-semibold text-secondary">{t('dashboard.todays_glucose')}</h2>
            <Link to="/insights" className="text-sm text-primary font-medium hover:underline flex items-center gap-1">
              {t('dashboard.view_insights')} <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="animate-pulse space-y-3">
              <div className="h-20 bg-orange-50 rounded-2xl" />
              <div className="h-12 bg-orange-50 rounded-2xl" />
            </div>
          ) : todayGlucose.length === 0 ? (
            <div className="text-center py-8 space-y-6">
              <div className="w-16 h-16 rounded-full bg-orange-50 mx-auto flex items-center justify-center">
                <Droplet className="w-8 h-8 text-primary/50" />
              </div>
              <p className="text-muted-foreground">{t('dashboard.no_glucose_readings')}</p>
              <Link to="/glucose">
                <Button className="rounded-full bg-primary text-white hover:bg-primary/90 mt-4" data-testid="log-first-glucose-btn">
                  {t('dashboard.log_first_reading')}
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Latest Reading Highlight */}
              {latestGlucose && latestDisplayValue && (
                <div className={`rounded-2xl p-4 ${getGlucoseStatus(latestDisplayValue).bg}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">{t('dashboard.latest_reading')}</p>
                      <div className="flex items-baseline gap-2">
                        <span className="text-4xl font-bold text-secondary">
                          {latestDisplayValue}
                        </span>
                        <span className="text-muted-foreground">{glucoseUnit}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-medium ${getGlucoseStatus(latestDisplayValue).color}`}>
                        {getGlucoseStatus(latestDisplayValue).label}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {getReadingTypeLabel(latestGlucose.reading_type)}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Today's Readings List */}
              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground">{t('dashboard.all_readings_today')} ({todayGlucose.length})</p>
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {todayGlucose.map((log, index) => {
                    const displayVal = getDisplayValue(log);
                    const status = getGlucoseStatus(displayVal);
                    const time = new Date(log.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                    return (
                      <div
                        key={log.log_id || index}
                        className={`flex-shrink-0 rounded-xl p-3 text-center ${status.bg}`}
                      >
                        <p className="text-lg font-bold text-secondary">{displayVal}</p>
                        <p className="text-xs text-muted-foreground">{time}</p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Average */}
              {averageGlucose && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{t('dashboard.todays_average')}</span>
                  <span className="font-semibold text-secondary">{averageGlucose} {glucoseUnit}</span>
                </div>
              )}
            </div>
          )}
        </Card>}

        {/* My Challenge Card (wellness users only) */}
        {challengeData && (
          <Card className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-3xl p-5 text-white" data-testid="my-challenge-card">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Zap className="w-5 h-5" />
                <h2 className="font-heading font-bold">{challengeData.program?.name || "My Challenge"}</h2>
              </div>
              <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">
                {challengeData.program?.sugar_target_grams}g/day target
              </span>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-white/10 rounded-xl p-3 text-center">
                <Flame className="w-5 h-5 mx-auto mb-1 text-yellow-300" />
                <p className="text-xl font-bold">{challengeData.enrollment?.streak_count || 0}</p>
                <p className="text-[10px] text-white/70">Day Streak</p>
              </div>
              <div className="bg-white/10 rounded-xl p-3 text-center">
                <Target className="w-5 h-5 mx-auto mb-1 text-emerald-300" />
                <p className="text-xl font-bold">{challengeData.enrollment?.total_days_logged || 0}</p>
                <p className="text-[10px] text-white/70">Days Logged</p>
              </div>
              <div className="bg-white/10 rounded-xl p-3 text-center">
                <Trophy className="w-5 h-5 mx-auto mb-1 text-yellow-300" />
                <p className="text-xl font-bold">{challengeData.enrollment?.badges?.length || 0}</p>
                <p className="text-[10px] text-white/70">Badges</p>
              </div>
            </div>
            {challengeData.enrollment?.badges?.length > 0 && (
              <div className="flex gap-1.5 mt-3 flex-wrap">
                {challengeData.enrollment.badges.map((b) => (
                  <span key={b} className="text-[10px] bg-white/20 px-2 py-0.5 rounded-full capitalize">
                    {b.replace(/_/g, " ")}
                  </span>
                ))}
              </div>
            )}
            {challengeData.dietitian && (
              <div className="mt-3 pt-3 border-t border-white/20 flex items-center gap-2">
                <Stethoscope className="w-4 h-4 text-white/70" />
                <span className="text-xs text-white/70">Coach: <span className="text-white font-medium">{challengeData.dietitian.name}</span></span>
              </div>
            )}
          </Card>
        )}

        {/* Coach Feedback on Recent Meals */}
        {mealFeedback.length > 0 && (
          <Card className="bg-white rounded-3xl border border-emerald-200/50 p-5 space-y-3" data-testid="coach-feedback-card">
            <div className="flex items-center gap-2 mb-1">
              <Stethoscope className="w-4 h-4 text-emerald-600" />
              <h3 className="font-heading font-semibold text-secondary text-sm">Coach Feedback</h3>
            </div>
            {mealFeedback.slice(0, 3).map((meal) => (
              <div key={meal.meal_id} className="bg-emerald-50/50 rounded-xl p-3 space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">{meal.meal_type || "Meal"} — {new Date(meal.timestamp).toLocaleDateString()}</span>
                  <div className="flex gap-0.5">
                    {[1,2,3,4,5].map(s => (
                      <svg key={s} className={`w-3 h-3 ${s <= meal.feedback.rating ? "text-yellow-400 fill-yellow-400" : "text-gray-200"}`} viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                    ))}
                  </div>
                </div>
                {meal.feedback.comment && (
                  <p className="text-sm text-secondary italic">"{meal.feedback.comment}"</p>
                )}
                <p className="text-[10px] text-muted-foreground">— {meal.feedback.physician_name}</p>
              </div>
            ))}
          </Card>
        )}

        {/* Challenge Rankings — 3 Categories */}
        {challengeRankings && (
          <Card className="bg-white rounded-3xl border border-amber-200/50 p-5 space-y-4" data-testid="challenge-rankings-card">
            <div className="flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-500" />
              <h3 className="font-heading font-semibold text-secondary">My Rankings</h3>
              <span className="ml-auto text-xs text-muted-foreground">{challengeRankings.total_participants} participants</span>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {/* Consistency */}
              <div className="bg-blue-50 rounded-xl p-3 text-center space-y-1">
                <Flame className="w-5 h-5 mx-auto text-blue-500" />
                <p className="text-xs font-medium text-muted-foreground">Consistency</p>
                <div className="flex items-center justify-center gap-1">
                  <Hash className="w-3 h-3 text-blue-600" />
                  <span className="text-2xl font-bold text-blue-700">{challengeRankings.rankings.consistency?.rank || "—"}</span>
                </div>
                <p className="text-[10px] text-muted-foreground">{challengeRankings.rankings.consistency?.perfect_days || 0} perfect days</p>
              </div>
              {/* Low Calorie */}
              <div className="bg-emerald-50 rounded-xl p-3 text-center space-y-1">
                <Target className="w-5 h-5 mx-auto text-emerald-500" />
                <p className="text-xs font-medium text-muted-foreground">Low Calorie</p>
                <div className="flex items-center justify-center gap-1">
                  <Hash className="w-3 h-3 text-emerald-600" />
                  <span className="text-2xl font-bold text-emerald-700">{challengeRankings.rankings.low_calorie?.rank || "—"}</span>
                </div>
                <p className="text-[10px] text-muted-foreground">{challengeRankings.rankings.low_calorie?.avg_calories || 0} avg kcal</p>
              </div>
              {/* Health Hero */}
              <div className="bg-amber-50 rounded-xl p-3 text-center space-y-1">
                <Trophy className="w-5 h-5 mx-auto text-amber-500" />
                <p className="text-xs font-medium text-muted-foreground">Health Hero</p>
                <div className="flex items-center justify-center gap-1">
                  <Hash className="w-3 h-3 text-amber-600" />
                  <span className="text-2xl font-bold text-amber-700">{challengeRankings.rankings.health_hero?.rank || "—"}</span>
                </div>
                <p className="text-[10px] text-muted-foreground">{challengeRankings.rankings.health_hero?.avg_health_score || 0}/5 score</p>
              </div>
            </div>
          </Card>
        )}

        {/* Recent Meals */}
        <Card className="bg-white rounded-3xl border border-orange-100/50 p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-lg font-semibold text-secondary">{t('dashboard.recent_meals')}</h2>
            <Link to="/meal-analysis" className="text-sm text-primary font-medium hover:underline flex items-center gap-1">
              {t('dashboard.analyze_meal')} <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="animate-pulse space-y-3">
              {[1, 2].map((i) => (
                <div key={i} className="h-16 bg-orange-50 rounded-2xl" />
              ))}
            </div>
          ) : recentMeals.length === 0 ? (
            <div className="text-center py-8 space-y-6">
              <p className="text-muted-foreground">{t('dashboard.no_meals_analyzed')}</p>
              <Link to="/meal-analysis">
                <Button variant="outline" className="rounded-full mt-4" data-testid="analyze-first-meal-btn">
                  {t('dashboard.analyze_first_meal')}
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {recentMeals.map((meal) => (
                <div
                  key={meal.meal_id}
                  className="flex items-center justify-between p-3 rounded-2xl bg-orange-50/50 hover:bg-orange-50 transition-colors"
                >
                  <div className="flex-1">
                    <p className="font-medium text-secondary">
                      {meal.food_details?.map(f => f.name).join(", ") || "Meal"}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {Math.round(meal.total_carbs || 0)}g carbs • {Math.round(meal.total_calories || 0)} kcal
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">
                      {new Date(meal.timestamp).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        
        {/* Bottom padding for navigation */}
        <div className="h-20"></div>
      </main>

      {/* Bottom Navigation */}
      <BottomNav />

      {/* Paywall Modal */}
      <Paywall
        isOpen={showPaywall}
        onClose={() => setShowPaywall(false)}
        trigger={paywallTrigger}
        usageData={usageData?.meal_analysis || usageData?.lana_questions}
      />

      {/* Logout Confirmation Dialog */}
      <LogoutConfirmDialog
        isOpen={showLogoutDialog}
        onClose={() => setShowLogoutDialog(false)}
        onConfirm={handleLogout}
        loading={logoutLoading}
      />
    </div>
  );
};

export default Dashboard;
